import { ComponentType } from '@angular/cdk/portal';

/**
 * Every component that can be hosted in a tab, keyed by the string written into the stored layout.
 * Renaming a key drops that tab for everyone with a live session, so the spec pins the list.
 *
 * Every loader is a dynamic `import()`, even where the component already sits in the main chunk:
 * `ChecklistEditComponent` is deliberately kept out of it, and a static entry would pull it back in.
 */
// biome-ignore format: one entry per pair of lines — key, then loader — keeps the list scannable.
export const TAB_COMPONENT_LOADERS = {
  'app-users': () =>
    import('@/pages/app-users/app-users.component').then((m) => m.AppUsersComponent),
  'app-user-detail': () =>
    import('@/pages/app-users/app-user-detail/app-user-detail.component').then((m) => m.AppUserDetailComponent),
  certificates: () =>
    import('@/pages/certificates/certificates.component').then((m) => m.CertificatesComponent),
  'certificate-edit': () =>
    import('@/pages/certificates/certificate-edit/certificate-edit.component').then((m) => m.CertificateEditComponent),
  'certificate-review-detail': () =>
    import('@/pages/certificates/certificate-review-detail/certificate-review-detail.component').then((m) => m.CertificateReviewDetailComponent),
  checklists: () =>
    import('@/pages/checklists/checklists.component').then((m) => m.ChecklistsComponent),
  'checklist-edit': () =>
    import('@/pages/checklists/checklist-edit/checklist-edit.component').then((m) => m.ChecklistEditComponent),
  'day-registration-detail': () =>
    import('@/pages/projects/project-day-registrations/day-registration-detail/day-registration-detail.component').then((m) => m.DayRegistrationDetailComponent),
  'email-log': () =>
    import('@/pages/email-log/email-log.component').then((m) => m.EmailLogComponent),
  organisations: () =>
    import('@/pages/organisations/organisations.component').then((m) => m.OrganisationsComponent),
  'organisation-edit': () =>
    import('@/pages/organisations/organisation-edit/organisation-edit.component').then((m) => m.OrganisationEditComponent),
  'organisation-employees': () =>
    import('@/pages/organisations/organisation-employees/organisation-employees.component').then((m) => m.OrganisationEmployeesComponent),
  'organisation-role-assignments': () =>
    import('@/pages/organisations/organisation-role-assignments/organisation-role-assignments.component').then((m) => m.OrganisationRoleAssignmentsComponent),
  'organisation-safety-requirement': () =>
    import('@/pages/organisations/organisation-safety-requirement/organisation-safety-requirement.component').then((m) => m.OrganisationSafetyRequirementComponent),
  persons: () =>
    import('@/pages/persons/persons.component').then((m) => m.PersonsComponent),
  'person-edit': () =>
    import('@/pages/persons/person-edit/person-edit.component').then((m) => m.PersonEditComponent),
  'person-role-assignments': () =>
    import('@/pages/persons/person-role-assignments/person-role-assignments.component').then((m) => m.PersonRoleAssignmentsComponent),
  'persons-review': () =>
    import('@/pages/persons/persons-review/persons-review.component').then((m) => m.PersonsReviewComponent),
  projects: () =>
    import('@/pages/projects/projects.component').then((m) => m.ProjectsComponent),
  'project-edit': () =>
    import('@/pages/projects/project-edit/project-edit.component').then((m) => m.ProjectEditComponent),
  'project-day-registrations': () =>
    import('@/pages/projects/project-day-registrations/project-day-registrations.component').then((m) => m.ProjectDayRegistrationsComponent),
  'project-alerts': () =>
    import('@/pages/projects/project-alerts/project-alerts.component').then((m) => m.ProjectAlertsComponent),
  'project-role-assignments': () =>
    import('@/pages/projects/project-role-assignments/project-role-assignments.component').then((m) => m.ProjectRoleAssignmentsComponent),
  'project-safety-requirement': () =>
    import('@/pages/projects/project-safety-requirement/project-safety-requirement.component').then((m) => m.ProjectSafetyRequirementComponent),
  'safety-instructions': () =>
    import('@/pages/safety-instructions/safety-instructions.component').then((m) => m.SafetyInstructionsComponent),
  'safety-instruction-edit': () =>
    import('@/pages/safety-instructions/safety-instruction-edit/safety-instruction-edit.component').then((m) => m.SafetyInstructionEditComponent),
  'safety-reports': () =>
    import('@/pages/safety-reports/safety-reports.component').then((m) => m.SafetyReportsComponent),
  'safety-report-create': () =>
    import('@/pages/safety-reports/safety-report-create/safety-report-create.component').then((m) => m.SafetyReportCreateComponent),
  'safety-report-detail': () =>
    import('@/pages/safety-report-detail/safety-report-detail.component').then((m) => m.SafetyReportDetailComponent),
  thesaurus: () =>
    import('@/pages/thesaurus/thesaurus.component').then((m) => m.ThesaurusComponent),
  toolboxes: () =>
    import('@/pages/toolboxes/toolboxes.component').then((m) => m.ToolboxesComponent),
  'toolbox-edit': () =>
    import('@/pages/toolboxes/toolbox-edit/toolbox-edit.component').then((m) => m.ToolboxEditComponent),
  'toolbox-analysis': () =>
    import('@/pages/toolboxes/toolbox-analysis/toolbox-analysis.component').then((m) => m.ToolboxAnalysisComponent),
  'toolbox-organisation-analysis': () =>
    import('@/pages/toolboxes/toolbox-organisation-analysis/toolbox-organisation-analysis.component').then((m) => m.ToolboxOrganisationAnalysisComponent),
  'toolbox-meeting-detail': () =>
    import('@/pages/toolboxes/toolbox-meeting-detail/toolbox-meeting-detail.component').then((m) => m.ToolboxMeetingDetailComponent),
} satisfies Record<string, () => Promise<ComponentType<unknown>>>;

export type TabKey = keyof typeof TAB_COMPONENT_LOADERS;

/**
 * A component that may be opened in a tab. Every open path takes this rather than a bare
 * `ComponentType`, so a page that is not registered fails to compile instead of failing to restore.
 */
export type TabHost = ComponentType<unknown> & { readonly tabKey: TabKey };

/** Narrow an untrusted key from a persisted snapshot. Guards against `__proto__` and friends. */
export function isTabKey(key: unknown): key is TabKey {
  return typeof key === 'string' && Object.hasOwn(TAB_COMPONENT_LOADERS, key);
}
